<script>


export default {
	name: 'LeafletMarker',
	inject: {
		leaflet: 'leaflet',
		state: 'state',
		getLayer: { default: null },
		pane: { default: null }
	},
	inheritAttrs: false,
	props: {
		latitude: {
			type: Number,
			default: 0,
			required: true
		},
		longitude: {
			type: Number,
			default: 0,
			required: true
		},
		modal: { // HTMLElement
			type: Object,
			default: null
		},
		modalOptions: {
			type: Object,
			default: () => ({})
		},
		/**
		 * L.divIcon
		*/
		icon: {
			type: Object,
			required: true
		},
		iconColor: String,
		context: { type: Object, },
		/** Prevent marker from being physically moved on map after drag */
		preventMarkerMovement: {
			type: Boolean,
			default: true
		},
		draggable: {
			type: Boolean,
			default: true
		},
		markerOptions: {
			type: Object,
			default: () => ({})
		},
		preventRefreshMarkersOnMoveMap: Boolean,
		visible: {
			type: Boolean,
			default: false
		},
		bringToFront: {
			type: Boolean,
			default: false
		},
		isSelected: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			marker: null,
			parent: null,
			selected: false,
			rendered: false,
			override: false,
			clonedNode: null,
			events: [
				'mouseout',
				'mouseover',
				'click',
				'popupopen',
				'popupclose',
				'contextmenu',
				'dblclick'
			]
		};
	},
	watch: {
		longitude() {
			this.reset();
		},
		latitude() {
			this.reset();
		},
		map() {
			this.reset();
		},
		icon(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.setIcon(newVal);
			}
		},
		iconColor(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.setIconColor(newVal);
			}
		},
		isSelected(newVal, oldVal) {
			if (newVal !== oldVal) this.setSelected(newVal);
		},
		bringToFront(newVal) {
			if (newVal) {
				this.putInFront();
			} else {
				this.putInBack();
			}
		}
	},
	beforeDestroy() {
		if (this.state.map && this.marker) {
			if (this.getLayer && this.getLayer() && this.getLayer().hasLayer(this.marker)) this.getLayer().removeLayer(this.marker);
			else if (this.state.map.hasLayer(this.marker)) this.marker.remove();
		}
	},
	methods: {
		reset() {
			this.override = false;
			this.rendered = false;
		},
		isInBounds() {
			return Boolean(this.state.map.getBounds().contains(this.leaflet.latLng(this.latitude, this.longitude)));
		},
		getMarker() {
			return this.marker;
		},
		addMarker() {
			if (!this.marker) return;
			if (this.getLayer && this.getLayer()) this.getLayer().addLayer(this.marker);
			else this.marker.addTo(this.state.map);
		},
		removeMarker() {
			this.marker && this.marker.remove();
		},
		eventCallback(event) {
			this.$emit(event.type, event);
		},
		dragstart() {
			this.clonedNode = this.marker._icon.cloneNode(true);
			this.clonedNode.style.position = 'fixed';
			this.clonedNode.style.zIndex = '99999';
			this.clonedNode.style.pointerEvents = 'none';
			this.clonedNode.style.transform = '';

			this.marker.setOpacity(0);

			document.body.appendChild(this.clonedNode);
			document.body.style.cursor = 'grab';

			window.addEventListener('mousemove', this.drag);
			window.addEventListener('mouseup', this.drop);
		},
		dragend() {
			this.$emit('drop');
			this.marker.setOpacity(1);

			document.body.style.cursor = '';
			document.body.style.userSelect = '';
			document.body.removeChild(this.clonedNode);
			this.clonedNode = null;

			window.removeEventListener('mousemove', this.drag);
			window.removeEventListener('mouseup', this.drop);
		},
		drop() {
			this.dragend();
			if (this.preventMarkerMovement) {
				this.marker.setOpacity(1);
				this.marker.setLatLng([ this.latitude, this.longitude ]);
			}
		},
		drag(e) {
			this.clonedNode.style.left = `${e.clientX}px`;
			this.clonedNode.style.top = `${e.clientY}px`;
			this.$emit('drag');
		},
		setSelected(value) {
			this.selected = value;
		},
		putInFront() {
			if (!this.marker) return;
			this.marker.setZIndexOffset(1000);
		},
		putInBack() {
			if (!this.marker) return;
			this.marker.setZIndexOffset(0);
		},
		setIcon(icon) {
			if (!this.marker) return;
			this.marker.setIcon(icon);
			if (!this.iconColor) return;
			this.setIconColor(this.iconColor);
		},
		setIconColor(color) {
			if (!this.marker) return;
			this.leaflet.DomUtil.addClass(this.marker._icon, color);
		},
		getHtml() {
			if (!this.marker) return null;
			return this.marker._icon;
		}
	},
	render() {
		if (this.state.map && !this.rendered) {
			if (this.marker) this.removeMarker();
			let options = {
				...this.markerOptions,
				draggable: this.draggable,
				icon: this.icon
			};
			if (this.pane) options.pane = this.pane;

			this.marker = this.leaflet.marker([ this.latitude, this.longitude ], options);
			this.events.forEach(event => this.marker.on(event, this.eventCallback));
			if (this.draggable)
				this.marker.on('dragstart', this.dragstart);

			this.addMarker();
			if (this.iconColor) this.setIconColor(this.iconColor);
			this.rendered = true;
			this.$emit('rendered');
		}

		if (this.marker && this.marker._icon) {
			if (this.bringToFront) this.putInFront();
			if (this.selected || this.isSelected) this.leaflet.DomUtil.addClass(this.marker._icon, 'selected');
			else this.leaflet.DomUtil.removeClass(this.marker._icon, 'selected');
		}

		return null;
	}
};
</script>

<style lang="scss">
@import '~@/components/evergreen/scss/colors';

.leaflet-marker-icon {
	outline: none;
}

.map-icon.neutral {
	.icon-fill {
		fill: currentColor;
		color: get-color($neutral, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($neutral, 'base');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($neutral, 'base');
		}
	}
}
.map-icon.dark-neutral {
	.icon-fill {
		fill: currentColor;
		color: get-color($neutral-scale, 'N7');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($neutral, 'dark');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($neutral, 'dark');
		}
	}
}

.map-icon.blue {
	.icon-fill {
		fill: currentColor;
		color: get-color($blue, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($blue, 'base');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($blue, 'base');
		}
	}
}

.map-icon.red {
	.icon-fill {
		fill: currentColor;
		color: get-color($red, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($red, 'base');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($red, 'base');
		}
	}
}

.map-icon.green {
	.icon-fill {
		fill: currentColor;
		color: get-color($green, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($green, 'base');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($green, 'base');
		}
	}
}

.map-icon.yellow {
	.icon-fill {
		fill: currentColor;
		color: get-color($yellow, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($yellow, 'dark');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($yellow, 'dark');
		}
	}
}

.map-icon.orange {
	.icon-fill {
		fill: currentColor;
		color: get-color($yellow, 'light');
	}
	.icon-outline {
		fill: currentColor;
		color: get-color($orange, 'base');
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: get-color($orange, 'base');
		}
	}
}

.map-icon.white {
	.icon-fill {
		fill: currentColor;
		color: transparent;
	}
	.icon-outline {
		fill: currentColor;
		color: white;
	}
	&.selected {
		.icon-fill {
			fill: currentColor;
			color: transparent;
		}
		.icon-outline {
			fill: white;
		}
		.marker-background {
			fill: white;
		}
	}
}
</style>
